'use client'
import { Image } from '@/components'
import { EditorReview } from '@/features/reviews/components'
import { IOrderItemsForReview, submitOrderReview } from '@/features/reviews/services'
import { getVND } from '@/utils/helpers'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box, Button, Rating, Stack, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ReviewHeader } from './review-header'
import { URL_IMG } from '@/constants/ui'

interface ReviewFormProps {
  orderData: IOrderItemsForReview
  protectCode: string
  onSubmitSuccess: (data: { status: string; verification_token?: string | null; message?: string }) => void
  onBack: () => void
}

export const ReviewForm = ({ orderData, protectCode, onSubmitSuccess, onBack }: ReviewFormProps) => {
  const editorRef = useRef<any>(null)

  // Local states
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const calculateCashback = () => {
    return getVND(orderData.refund_amount, ' ')
  }

  // Handler submit review
  const handleSubmit = async () => {
    if (!comment.trim() || rating === 0) {
      setErrorMessage('Vui lòng nhập đánh giá và chọn số sao')
      return
    }

    setLoading(true)
    setErrorMessage('')

    try {
      // Lấy media URLs từ EditorReview
      const mediaUrls = (await editorRef.current?.getMediaUrls()) || []

      // Phân loại media thành images và videos
      const images: string[] = []
      const videos: string[] = []

      mediaUrls.forEach((url: string) => {
        // Kiểm tra extension để phân loại
        const ext = url.split('.').pop()?.toLowerCase()
        if (['mp4', 'webm', 'ogg'].includes(ext || '')) {
          videos.push(url)
        } else {
          images.push(url)
        }
      })

      // Submit review
      const result = await submitOrderReview({
        increment_id: orderData.increment_id,
        protect_code: protectCode,
        description: comment,
        rating_stars: rating,
        images,
        videos,
        refund_amount: orderData.refund_amount.toString(),
      })

      if (result.status === 'error') {
        setErrorMessage(result.message || 'Không thể gửi đánh giá')
        setLoading(false)
        return
      }

      if (result.data) {
        // Reset editor sau khi submit thành công
        editorRef.current?.reset()
        setComment('')
        setRating(5)

        // Callback để xử lý OTP hoặc success
        onSubmitSuccess(result.data)
      }
    } catch (error) {
      console.error('Submit review error:', error)
      toast.error('Có lỗi xảy ra khi gửi đánh giá')
      setErrorMessage('Có lỗi xảy ra khi gửi đánh giá')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: '520px' }}>
      {/* Header with Logo & Title */}
      <ReviewHeader
        title={
          <>
            Đánh giá sản phẩm
            <br />
            Hoàn tiền lên đến {orderData.refund_percentage}%
          </>
        }
        subtitle={
          <>
            Chúng tôi cảm ơn bạn vì đã tin tưởng và mua hàng từ HYDRA.{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              Hãy để lại cảm nghĩ của bạn
            </Box>{' '}
            về sản phẩm và chia sẻ đến mọi người!
          </>
        }
      />

      {/* Product Info */}
      {orderData.items_to_review.map((item, index) => (
        <Stack key={`${item.product_sku}-${index}`} direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {item.product_image?.url ? (
              <Image
                src={item.product_image.url}
                alt={item.product_name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                }}
              >
                IMG
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{item.product_name}</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 300 }}>
              {item.options.map((opt) => opt.value).join(', ')} x{item.qty_ordered}
            </Typography>
          </Box>
        </Stack>
      ))}

      {/* Rating */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontSize: '14px', fontWeight: 300 }}>Đánh giá:</Typography>
        <Rating
          value={rating}
          onChange={(_, newValue) => setRating(newValue || 0)}
          sx={{
            '& .MuiRating-iconFilled': {
              color: '#FFB400',
            },
          }}
        />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>({rating} sao)</Typography>
      </Stack>

      {/* Comment */}
      <Box>
        <Typography sx={{ fontSize: '14px', fontWeight: 300, mb: 1 }}>Nhận xét:</Typography>
        <EditorReview
          ref={editorRef}
          value={comment}
          onChange={setComment}
          placeholder="Hãy chia sẻ nhận xét cho sản phẩm này bạn nhé!"
          disabled={loading}
          rows={2}
          maxImages={5}
          maxVideos={1}
        />
      </Box>

      {/* Action Buttons */}
      <Stack spacing={1}>
        {/* Error Message */}
        {errorMessage && (
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 300,
              color: '#F04438',
              textAlign: 'center',
              mb: 1,
            }}
          >
            {errorMessage}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          size="large"
          loading={loading}
          disabled={!comment.trim() || rating === 0 || loading}
          sx={{
            color: 'white',
            borderRadius: '999px',
            height: '40px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {`Gửi đánh giá +${calculateCashback()}`} Xu{' '}
        </Button>

        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={onBack}
          disabled={loading}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: '999px',
            height: '40px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Quay về
        </Button>
      </Stack>
    </Stack>
  )
}
